import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { CiCircleCheck } from "react-icons/ci";
import { IoSparkles } from "react-icons/io5";
import { IoMdClose } from "react-icons/io";
import { subscribe, verifySubscription } from "../../../Service/user";
import { load } from "@cashfreepayments/cashfree-js";
import { plans } from "./data";
import "./subscriptionPop.scss";
import axios from "axios";
import { loginSuccess } from "../../../Store/features/user/userSlice";
import { toast } from "react-hot-toast";

const SubscriptionPopup = ({ isOpen, onClose, fromEmailViewComponent = false, fromCampaign = false, fromInvestor = false }) => {
	const [orderId, setOrderId] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [cashfree, setCashfree] = useState(null);
	const navigate = useNavigate();
	const loggedInUser = useSelector((state) => state.user.loggedInUser);
	const dispatch = useDispatch();

	// Initialize Cashfree SDK
	useEffect(() => {
		const initializeSDK = async () => {
			try {
				// Production mode (commented out)
				// const cashfreeInstance = await load({
				// 	mode: "production",
				// });

				// Sandbox/Test mode
				const cashfreeInstance = await load({
					mode: "production",
				});
				setCashfree(cashfreeInstance);
			} catch (error) {
				console.error("Failed to initialize Cashfree SDK:", error);
				setError(
					"Payment system initialization failed. Please try again later."
				);
			}
		};

		if (isOpen) {
			initializeSDK();
		}

		return () => {
			// Cleanup if needed
			setError(null);
			setIsLoading(false);
		};
	}, [isOpen]);

	const handlePurchase = async (plan) => {
		if (!cashfree) {
			setError("Payment system is not ready. Please try again.");
			return;
		}
	
		try {
			setIsLoading(plan.subscriptionType); // Only set loading for the clicked button
			setError(null);

			if (plan.subscriptionType !== "Basic") {
				const priceToCharge = plan.discountedPrice || plan.price;
				const subscriptionData = {
					...plan,
					price: priceToCharge
				};

				const res = await subscribe(subscriptionData);

				if (!res?.order_id || !res?.payment_session_id) {
					throw new Error("Invalid payment session response");
				}

				setOrderId(res.order_id);

				const checkoutOptions = {
					paymentSessionId: res.payment_session_id,
					redirectTarget: "_modal",
				};

				try {
					const cashfreeResponse = await cashfree.checkout(checkoutOptions);
					const verifyResponse = await verifySubscription(
						{ orderId: res.order_id },
					);
					if (verifyResponse.paymentStatus === "SUCCESS") {
							// Update the user in Redux store
							dispatch(loginSuccess(verifyResponse.user));
								
							// Update the stored user data in localStorage
							const accountType = verifyResponse.user.isInvestor === "true" ? "InvestorAccounts" : "StartupAccounts";
							const storedAccounts = JSON.parse(localStorage.getItem(accountType)) || [];
							const updatedAccounts = storedAccounts.map(account => {
								if (account.user._id === verifyResponse.user._id) {
									return { ...account, user: verifyResponse.user };
								}
								return account;
							});
							localStorage.setItem(accountType, JSON.stringify(updatedAccounts));
						onClose();
						toast.success("Subscription activated successfully!");
					} else {
						toast.error("Payment failed. Please try again.");
					}
				} catch (error) {
					console.error("Checkout error:", error);
					throw new Error("Failed to initialize payment checkout");
				}
			}
		} catch (error) {
			console.error("Purchase error:", error);
			setError(error.message || "Failed to process payment. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};
	
	

	const getSubscriptionColor = (type) => {
		switch (type) {
			case "Basic":
				return "white";
			case "Pro":
				return "gold";
			default:
				return "white";
		}
	};

	if (!isOpen) return null;

	const renderPlanFeatures = (features, subscriptionType) => (
		<ul className="sp-features">
			{features.map((feature, idx) => (
				<li key={idx} className="sp-feature-item">
					<CiCircleCheck
						className={`sp-feature-icon ${
							subscriptionType === "Standard" ? "highlight" : ""
						}`}
					/>
					<span className="sp-feature-text">{feature}</span>
				</li>
			))}
		</ul>
	);

	const renderPlanPrice = (price, discountedPrice) => (
		<p className="sp-price">
			{price === "0" ? (
				"Free Forever"
			) : discountedPrice ? (
				<>
					<span className="sp-original-price">₹{price}</span>
					<span className="sp-discount-badge">50% OFF</span>
					<span className="sp-final-price">
						₹{discountedPrice}
						<span className="sp-price-period">/year</span>
					</span>
				</>
			) : (
				<>
					₹{price}
					<span className="sp-price-period">/month</span>
				</>
			)}
		</p>
	);

	const renderPlanButton = (plan) => {
		if (isLoading === plan.subscriptionType) {
			return (
				<button className="sp-purchase-button sp-button-disabled" disabled>
					Processing...
				</button>
			);
		}
	
		if (plan.subscriptionType === "Basic") {
			return (
				<button className="sp-purchase-button sp-button-disabled" disabled>
					Current Plan
				</button>
			);
		}
	
		return (
			<button
				className="sp-purchase-button"
				onClick={() => handlePurchase(plan)}
				disabled={isLoading}
			>
				Purchase Now
			</button>
		);
	};
	

	return (
		<div className="sp-container">
			<div className="sp-overlay" onClick={onClose} />
			<div className="sp-modal">
				<button
					className="sp-close-button"
					onClick={onClose}
					aria-label="Close"
				>
					<IoMdClose />
				</button>

				{error && (
					<div className="sp-error-message">
						{error}
						<button onClick={() => setError(null)}>Dismiss</button>
					</div>
				)}
				{fromEmailViewComponent && (
					<h2 className="sp-title">To access the contact details, please subscribe to a plan</h2>
				)}
				{fromCampaign && (
					<h2 className="sp-title">To create a campaign, please subscribe to our pro plan</h2>
				)}
				{!fromCampaign && !fromEmailViewComponent && (
					<h2 className="sp-title">Simple and Affordable Pricing Plans</h2>
				)}
				<div className={`sp-plans ${fromCampaign ? 'sp-plans-two' : ''}`}>
					{plans
						.filter(plan => {
							if (fromInvestor) {
								return ['Basic', 'Standard'].includes(plan.subscriptionType);
							}
							if (fromCampaign) {
								return ['Basic', 'Pro'].includes(plan.subscriptionType);
							}
							return true;
						})
						.map((plan, index) => {
						
							const displayFeatures = fromCampaign && plan.subscriptionType === 'Pro' 
								? [...plans.find(p => p.subscriptionType === 'Standard')?.features || [], ...plan.features]
								: plan.features;

							return (
								<div
									key={index}
									className={`sp-plan ${plan.subscriptionType === "Pro" ? "sp-plan-premium" : ""} ${
										fromCampaign ? 'sp-plan-wide' : ''
									}`}
								>
									<div className="sp-plan-header">
										<h3
											className="sp-plan-title"
											style={{ color: getSubscriptionColor(plan.subscriptionType) }}
										>
											{plan.subscriptionType}
											{plan.subscriptionType !== "Basic" && (
												<IoSparkles className="sp-sparkle" />
											)}
										</h3>
										{renderPlanPrice(plan.price, plan.discountedPrice)}
									</div>

									<p className="sp-description">{plan.description}</p>
									<hr className="sp-divider" />
									{renderPlanFeatures(displayFeatures, plan.subscriptionType)}

									{renderPlanButton(plan)}
								</div>
							);
						})}
				</div>
			</div>
		</div>
	);
};

export default SubscriptionPopup;
